import React, { useState, useRef, useCallback } from 'react';
import type { ReactElement, MouseEvent, CSSProperties } from 'react';
import Draggable from 'react-draggable';
import type { DraggableData, DraggableEvent } from 'react-draggable';

import { BOTTOM_MARGIN, MINIMIZED_PLAYER_HEIGHT } from '@/components/ui/@DesignSystem/VideoPlayer/utils/constants';
import * as S from '../../styles';

const DRAGGABLE_STYLE: CSSProperties = {
	width: 'fit-content',
	willChange: 'transform',
	touchAction: 'none',
	userSelect: 'none'
};

const MODAL_STYLE: CSSProperties = { top: 0, margin: 0 };

interface DraggablePlayerProps {
	onClose?: () => void;
	closeIcon?: ReactElement;
	getContainer?: () => HTMLElement;
	children: JSX.Element | JSX.Element[];
	onClick?: (event: MouseEvent) => void;
	onMouseEnter?: (event: MouseEvent) => void;
	onMouseLeave?: (event: MouseEvent) => void;
}

export function DraggablePlayer({
	children,
	closeIcon,
	onClose,
	onClick,
	getContainer,
	onMouseEnter,
	onMouseLeave
}: Readonly<DraggablePlayerProps>): JSX.Element {
	const [bounds, setBounds] = useState<{ left: number; top: number; bottom: number; right: number }>();
	const draggableRef = useRef<HTMLDivElement>(null);

	const defaultPosition = {
		x: BOTTOM_MARGIN,
		y: window.innerHeight - MINIMIZED_PLAYER_HEIGHT - BOTTOM_MARGIN
	};

	const handleOnStart = useCallback((_event: DraggableEvent, uiData: DraggableData): void => {
		const { clientWidth: viewportWidth, clientHeight: viewportHeight } = window.document.documentElement;
		const targetRect = draggableRef.current?.getBoundingClientRect();

		if (!targetRect) {
			return;
		}

		const maxDragLeft = -targetRect.left + uiData.x;
		const maxDragRight = viewportWidth - (targetRect.right - uiData.x);
		const maxDragUp = -targetRect.top + uiData.y;
		const maxDragDown = viewportHeight - (targetRect.bottom - uiData.y);

		setBounds({
			left: maxDragLeft,
			right: maxDragRight,
			top: maxDragUp,
			bottom: maxDragDown
		});
	}, []);

	return (
		<S.DraggableVideoModal
			closable
			visible={true}
			mask={false}
			width="20rem"
			footer={null}
			closeIcon={closeIcon}
			onCancel={onClose}
			getContainer={getContainer}
			style={MODAL_STYLE}
			modalRender={(modal) => (
				<Draggable defaultPosition={defaultPosition} bounds={bounds} nodeRef={draggableRef} onStart={handleOnStart}>
					<div
						ref={draggableRef}
						style={DRAGGABLE_STYLE}
						onClick={onClick}
						onMouseEnter={onMouseEnter}
						onMouseLeave={onMouseLeave}
					>
						{modal}
					</div>
				</Draggable>
			)}
		>
			{children}
		</S.DraggableVideoModal>
	);
}
